import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Container, SectionLabel } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import { Rocket, HeartHandshake, ShieldCheck, ArrowRight, Flag, Mail, Search, SearchX } from "lucide-react";

const FAQ_GROUPS = [
  {
    label: "Getting started",
    icon: Rocket,
    items: [
      {
        q: "What is FightFund and how does it work?",
        a: "FightFund is a U.S.-only crowdfunding platform built on trust: fast for donors, strict for fundraisers, and controlled before payout. Organizers create a campaign, complete identity and payout onboarding, and are reviewed before going live. Donors give in seconds with guest checkout, and funds are released to organizers only through Stripe once our review conditions are met.",
      },
      {
        q: "Is it free to start a fundraiser? What fees does FightFund charge?",
        a: "Creating a campaign is free — there is no upfront or monthly cost. When funds are paid out, FightFund charges a 5% platform fee (3% for verified Pro organizers), plus a Stripe processing fee on each donation. Donors can also add an optional tip to FightFund at checkout, but tips are never required. There are no hidden fees — the only charges are the platform fee and Stripe's processing fee, and both are shown up front.",
      },
      {
        q: "What is FightFund Pro and the nonprofit tier?",
        a: "FightFund Pro is our tier for nonprofits and high-volume organizers. Pro organizers pay a reduced 3% platform fee (versus 5% standard), appear in the Pro directory, and unlock nonprofit verification and additional tools. You can explore it from the Pro and For Nonprofits pages.",
      },
    ],
  },
  {
    label: "Donating",
    icon: HeartHandshake,
    items: [
      {
        q: "Do I need an account to donate?",
        a: "No. Donations take seconds with guest checkout — just enter an amount and your email to receive a receipt. You can optionally create an account afterward to track your giving from your dashboard.",
      },
      {
        q: "What is the optional tip, and where does it go?",
        a: "At checkout, donors can add an optional tip to FightFund. Tips are completely optional and help keep the platform running. They are separate from the amount that goes to the campaign, and you can set the tip to 0%.",
      },
      {
        q: "What payment methods do you accept? Is FightFund available outside the U.S.?",
        a: "We accept major credit and debit cards as well as cryptocurrency, both processed securely through Stripe and settled in U.S. dollars. FightFund currently operates in the United States only, in USD.",
      },
      {
        q: "Are donations tax-deductible?",
        a: "Only donations to verified nonprofit campaigns run by a qualifying 501(c)(3) organization may be tax-deductible. Donations to personal or individual campaigns are generally not tax-deductible. FightFund is not a tax advisor — please consult a professional for your specific situation.",
      },
      {
        q: "Can I get a refund, or what if a campaign looks fraudulent?",
        a: "Donations are generally final, but if something is not right, email us at help@fightfund.com and we will look into it. If you believe a campaign is fraudulent or misleading, use the Report a Campaign button — every report enters our Trust & Safety workflow and can pause payouts while we investigate.",
      },
    ],
  },
  {
    label: "Payouts & trust",
    icon: ShieldCheck,
    items: [
      {
        q: "How and when do organizers receive their funds?",
        a: "Organizers request a payout from their dashboard when ready. Funds move only through Stripe rails after our review conditions are met. First payouts hold for a minimum of 7 days after settlement and review. A 5% platform fee (3% for Pro) plus Stripe processing is deducted, and the net amount is transferred to the organizer's connected Stripe account.",
      },
      {
        q: "How do you verify organizers and review campaigns?",
        a: "Every campaign is reviewed before it can accept public donations. Organizers verify their legal name, email, and phone, and connect a U.S. payout account through Stripe. Verification evidence scales with category and risk — higher goals and sensitive categories require more. We also monitor for duplicate content, complaints, and beneficiary mismatches on an ongoing basis.",
      },
    ],
  },
];

const ALL_ITEMS = FAQ_GROUPS.flatMap((g) => g.items);

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: ALL_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
};

export default function FAQ() {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filteredGroups = FAQ_GROUPS
    .map((group, gi) => ({
      ...group,
      gi,
      matches: group.items
        .map((it, ii) => ({ ...it, ii }))
        .filter((it) => !q || it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)),
    }))
    .filter((group) => group.matches.length > 0);
  const totalMatches = filteredGroups.reduce((n, g) => n + g.matches.length, 0);

  return (
    <div data-testid="faq-page">
      <Seo title="FAQ" description="Answers to the most common questions about donating, starting a fundraiser, fees, payouts, and trust on FightFund." />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
      </Helmet>

      <section className="border-b border-ff-border bg-ff-blue text-white">
        <Container className="py-16 lg:py-20">
          <SectionLabel><span className="text-white/60">Help center</span></SectionLabel>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Frequently asked questions
          </h1>
          <p className="mt-5 max-w-2xl text-white/70">
            Everything donors and organizers ask most — fees, payouts, verification, and how FightFund keeps
            fundraising fast for donors and strict for fundraisers.
          </p>
        </Container>
      </section>

      <section className="bg-white">
        <Container className="py-14 lg:py-16">
          <div className="mx-auto mb-10 max-w-3xl">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ff-medium" />
              <Input
                data-testid="faq-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions…"
                aria-label="Search FAQ"
                className="h-12 pl-10 text-base" />
            </div>
            {q && (
              <p className="mt-2 text-xs font-medium text-ff-medium" data-testid="faq-search-count">
                {totalMatches} {totalMatches === 1 ? "result" : "results"} for “{query}”
              </p>
            )}
          </div>

          {totalMatches === 0 ? (
            <div className="mx-auto max-w-3xl rounded-2xl border border-ff-border bg-[#F3F4F6] p-10 text-center" data-testid="faq-no-results">
              <SearchX className="mx-auto h-8 w-8 text-ff-medium" />
              <h3 className="mt-3 font-heading text-lg font-bold text-ff-blue">No matching questions</h3>
              <p className="mt-1 text-sm text-ff-dark">Try a different keyword, or reach out and we'll help directly.</p>
              <Button asChild variant="outline" className="mt-4 border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white">
                <Link to="/contact" data-testid="faq-no-results-contact">Contact us</Link>
              </Button>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-12">
              {filteredGroups.map((group) => (
                <div key={group.label} data-testid={`faq-group-${group.gi}`}>
                  <div className="mb-2 flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#ECFDF5] text-ff-green">
                      <group.icon className="h-5 w-5" />
                    </span>
                    <h2 className="font-heading text-xl font-bold text-ff-blue">{group.label}</h2>
                  </div>
                  <Accordion
                    type="multiple"
                    key={q ? `q-${q}` : "idle"}
                    defaultValue={q ? group.matches.map((it) => `g${group.gi}-i${it.ii}`) : []}
                    className="w-full">
                    {group.matches.map((item) => (
                      <AccordionItem key={item.q} value={`g${group.gi}-i${item.ii}`} className="border-ff-border">
                        <AccordionTrigger
                          data-testid={`faq-question-${group.gi}-${item.ii}`}
                          className="font-heading text-base font-bold text-ff-blue hover:no-underline">
                          {item.q}
                        </AccordionTrigger>
                        <AccordionContent
                          data-testid={`faq-answer-${group.gi}-${item.ii}`}
                          className="text-sm leading-relaxed text-ff-dark">
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>

      <section className="border-t border-ff-border bg-[#F3F4F6]">
        <Container className="flex flex-col items-center gap-4 py-16 text-center">
          <h2 className="font-heading text-2xl font-bold text-ff-blue sm:text-3xl">Still have questions?</h2>
          <p className="max-w-lg text-ff-dark">
            We're here to help. Reach out and our team will get back to you, or report a campaign if something
            looks off.
          </p>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
              <Link to="/contact" data-testid="faq-contact-cta"><Mail className="mr-2 h-4 w-4" /> Contact us <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-ff-red font-semibold text-ff-red hover:bg-ff-red hover:text-white">
              <Link to="/report-campaign" data-testid="faq-report-cta"><Flag className="mr-2 h-4 w-4" /> Report a Campaign</Link>
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}
